/**
 * Every HTTP path the web app calls, in one place so the integrator can reconcile
 * it with the backend router. Paths are relative to API_BASE (`/api/v1`).
 *
 * The agreed endpoint map is in the first block. Endpoints the UI needs that are
 * NOT in the agreed list are grouped at the bottom under "EXTRA" — each one is
 * optional: the UI degrades gracefully (hides the section / shows a notice) when
 * the backend answers 404.
 */
export const API_BASE = "/api/v1";

const e = encodeURIComponent;

export const EP = {
  // ── auth ──
  login: "/auth/login",
  refresh: "/auth/refresh",
  logout: "/auth/logout",
  me: "/auth/me",
  apiTokens: "/api-tokens",
  apiToken: (id: string) => `/api-tokens/${e(id)}`,

  // ── users (admin) ──
  users: "/users",
  user: (id: string) => `/users/${e(id)}`,
  userAssignments: (id: string) => `/users/${e(id)}/assignments`,

  // ── sites / fleets ──
  sites: "/sites",
  fleets: "/fleets",

  // ── robots ──
  robots: "/robots",
  robot: (id: string) => `/robots/${e(id)}`,
  robotRotateCredentials: (id: string) => `/robots/${e(id)}/credentials`,
  robotTelemetry: (id: string) => `/robots/${e(id)}/telemetry`,
  robotTelemetryHistory: (id: string) => `/robots/${e(id)}/telemetry/history`,
  robotTimeline: (id: string) => `/robots/${e(id)}/timeline`,
  robotErrors: (id: string) => `/robots/${e(id)}/errors`,
  robotCommands: (id: string) => `/robots/${e(id)}/commands`,
  robotControl: (id: string) => `/robots/${e(id)}/control`,
  robotControlLeases: (id: string) => `/robots/${e(id)}/control-leases`,
  controlLeaseRenew: (leaseId: string) => `/control-leases/${e(leaseId)}/renew`,
  controlLease: (leaseId: string) => `/control-leases/${e(leaseId)}`,
  robotEstop: (id: string) => `/robots/${e(id)}/estop`,
  /**
   * GET → ArmJogSessionDto (200) or 404 when the current lease + authority generation has no
   * confirmation; POST ArmJogSessionRequest → 201 ArmJogSessionDto: the lease holder confirms they
   * can see the arm (ADR-018 D7).
   */
  robotArmJogSession: (id: string) => `/robots/${e(id)}/arm/jog-session`,
  robotCalibrations: (id: string) => `/robots/${e(id)}/calibrations`,
  robotRecoveries: (id: string) => `/robots/${e(id)}/recoveries`,
  robotDeployments: (id: string) => `/robots/${e(id)}/config-deployments`,
  deploymentPreview: (id: string) => `/robots/${e(id)}/config-deployments/preview`,
  /** Rolls back the given (currently applied) deployment to the previous synced bundle. */
  deploymentRollback: (robotId: string, id: string) => `/robots/${e(robotId)}/config-deployments/${e(id)}/rollback`,
  robotDiagnostics: (id: string) => `/robots/${e(id)}/diagnostics`,
  /** POST StartMapImportRequest → MapImportDto; GET → MapImportDto[] (newest first). */
  robotMapImports: (id: string) => `/robots/${e(id)}/map-imports`,
  mapImport: (id: string) => `/map-imports/${e(id)}`,

  // ── missions ──
  missions: "/missions",
  mission: (id: string) => `/missions/${e(id)}`,
  missionAction: (id: string, action: "cancel" | "pause" | "resume") => `/missions/${e(id)}/${action}`,
  commandResolve: (id: string) => `/commands/${e(id)}/resolve`,
  /** Request cancellation of a running command (needs mission.cancel); the robot confirms the stop. */
  commandCancel: (id: string) => `/commands/${e(id)}/cancel`,

  // ── maps ──
  maps: "/maps",
  map: (id: string) => `/maps/${e(id)}`,
  mapVersions: (id: string) => `/maps/${e(id)}/versions`,
  mapVersionImage: (id: string, version: number) => `/maps/${e(id)}/versions/${version}/image`,
  mapVersionEdits: (id: string, version: number) => `/maps/${e(id)}/versions/${version}/edits`,
  mapWaypoints: (id: string) => `/maps/${e(id)}/waypoints`,
  waypoint: (mapId: string, id: string) => `/maps/${e(mapId)}/waypoints/${e(id)}`,
  mapPaths: (id: string) => `/maps/${e(id)}/paths`,
  path: (mapId: string, id: string) => `/maps/${e(mapId)}/paths/${e(id)}`,
  mapValidate: (id: string) => `/maps/${e(id)}/validate`,
  mapRoutePlans: (id: string) => `/maps/${e(id)}/route-plans`,
  mapRoutePlanLatest: (id: string) => `/maps/${e(id)}/route-plans/latest`,
  mapRoutePlanImport: (id: string) => `/maps/${e(id)}/route-plans/import`,
  mapRoutePlanRoutes: (id: string, planId: string) => `/maps/${e(id)}/route-plans/${e(planId)}/routes`,
  /** 3D layers of a map version (api.ts doc comment above MapLayerKind). GET → MapLayerDto[]; POST multipart file [+ allowMismatch]. */
  mapLayers: (id: string, version: number) => `/maps/${e(id)}/versions/${version}/layers`,
  /** DELETE → 204. */
  mapLayer: (id: string, version: number, layerId: string) => `/maps/${e(id)}/versions/${version}/layers/${e(layerId)}`,
  /** POINTCLOUD_PREVIEW bytes (contracts pointcloud.ts). */
  mapLayerPreview: (id: string, version: number, layerId: string) => `/maps/${e(id)}/versions/${version}/layers/${e(layerId)}/preview`,
  /** The original upload. */
  mapLayerDownload: (id: string, version: number, layerId: string) => `/maps/${e(id)}/versions/${version}/layers/${e(layerId)}/download`,

  // ── workflows ──
  workflows: "/workflows",
  workflow: (id: string) => `/workflows/${e(id)}`,

  // ── admin ──
  /** Read-only registry for any robot.view holder (monitoring, console, teleop limits). */
  registryRead: "/registry",
  registry: "/admin/ros-interfaces",
  registryEntry: (id: string) => `/admin/ros-interfaces/${e(id)}`,
  /** Read-only catalog for any robot.view holder. */
  errorCatalogRead: "/error-catalog",
  errorCatalog: "/admin/errors",
  errorDefinition: (code: string) => `/admin/errors/${e(code)}`,

  // ── audit ──
  audit: "/audit",

  // ── realtime ──
  realtimeTickets: "/realtime/tickets",
  /** WebSocket path; ticket passed as ?ticket=… */
  realtimeSocket: "/realtime",

  // ───────────────────────── EXTRA (not in the agreed endpoint list) ─────────────────────────

  /**
   * EXTRA: GET recovery attempt history for a robot → RecoveryAttemptDto[] | Page.
   * Same path as the agreed POST; the errors page hides the history table on 404.
   */
  robotRecoveryHistory: (id: string) => `/robots/${e(id)}/recoveries`,
  /** EXTRA: download one diagnostic artifact (binary). */
  diagnosticDownload: (robotId: string, artifactId: string) => `/robots/${e(robotId)}/diagnostics/${e(artifactId)}/download`,
} as const;
